import React from 'react';
import { CheckCircle, Ban, X, ClipboardCheck, Loader2 } from 'lucide-react';
import { cn } from '../../utils/cn';

/**
 * What can be done to a reservation, said in words.
 *
 * It used to be a tray of bare glyphs — a tick, a cross, a clipboard — so the only way to learn
 * which one revoked a resident's reservation was to hover each in turn, or click one and find out.
 * At a counter, with someone waiting, that is the wrong moment to be guessing.
 *
 * The buttons stack in a column: a row carries between one and three of them depending on status,
 * and laid out side by side the widest case pushed the resident's name and unit out of the row.
 *
 * Which actions are OFFERED stays with the caller — the área detail deliberately has no Revogar
 * (that needs a reason and its own modal, and the detail list includes reservations that already
 * happened), while the Agendamentos tab has one.
 */

export interface BookingActionsProps {
  lightTheme: boolean;
  /** A request for this booking is in flight: every button waits, the one pressed spins. */
  processing?: boolean;
  onApprove?: () => void;
  onRevoke?: () => void;
  onCancel?: () => void;
  /** Opens/closes the check-in + guest-list panel. */
  onToggleDetails: () => void;
  detailsOpen: boolean;
  /** Drives the check-in button's colour — a booking already checked in reads as done. */
  checkedIn?: boolean;
}

const BookingActions: React.FC<BookingActionsProps> = ({
  lightTheme,
  processing = false,
  onApprove,
  onRevoke,
  onCancel,
  onToggleDetails,
  detailsOpen,
  checkedIn,
}) => {
  const base = 'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap';

  const tone = {
    approve: lightTheme
      ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
      : 'border-emerald-700/50 bg-emerald-900/20 text-emerald-400 hover:bg-emerald-900/40',
    revoke: lightTheme
      ? 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100'
      : 'border-red-700/50 bg-red-900/20 text-red-400 hover:bg-red-900/40',
    neutral: lightTheme
      ? 'border-gray-200 bg-white text-gray-700 hover:bg-gray-100'
      : 'border-gray-700 bg-gray-800/60 text-gray-300 hover:bg-gray-800',
    done: lightTheme
      ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
      : 'border-emerald-700/50 bg-emerald-900/20 text-emerald-400 hover:bg-emerald-900/40',
  };

  const Icon = ({ fallback: Fallback }: { fallback: React.ElementType }) =>
    processing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Fallback className="h-3.5 w-3.5" />;

  return (
    <div data-testid="booking-actions" className="flex flex-col items-stretch gap-1 flex-shrink-0">
      {onApprove && (
        <button type="button" onClick={onApprove} disabled={processing} title="Aprovar" className={cn(base, tone.approve)}>
          <Icon fallback={CheckCircle} />
          Aprovar
        </button>
      )}
      {onRevoke && (
        <button type="button" onClick={onRevoke} disabled={processing} title="Revogar" className={cn(base, tone.revoke)}>
          <Icon fallback={Ban} />
          Revogar
        </button>
      )}
      {onCancel && (
        <button type="button" onClick={onCancel} disabled={processing} title="Cancelar" className={cn(base, tone.neutral)}>
          <Icon fallback={X} />
          Cancelar
        </button>
      )}
      <button
        type="button"
        onClick={onToggleDetails}
        aria-expanded={detailsOpen}
        title={detailsOpen ? 'Fechar check-in e convidados' : 'Check-in e convidados'}
        className={cn(base, checkedIn ? tone.done : tone.neutral)}
      >
        <ClipboardCheck className="h-3.5 w-3.5" />
        {detailsOpen ? 'Fechar' : 'Check-in'}
      </button>
    </div>
  );
};

export default BookingActions;
